import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, CheckCircle, Calendar } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analytics = () => {
  const [approvedQuotes, setApprovedQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [stats, setStats] = useState({
    totalApproved: 0,
    totalRevenue: 0,
    thisMonth: 0,
    lastMonth: 0
  });

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/analytics/approved-quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const quotes = response.data;
      setApprovedQuotes(quotes);
      
      // Calculate stats
      const totalRevenue = quotes.reduce((sum, q) => sum + parseFloat(q.price || 0), 0);
      const now = new Date();
      const thisMonth = quotes.filter(q => {
        const quoteDate = new Date(q.approved_date || q.date);
        return quoteDate.getMonth() === now.getMonth() && quoteDate.getFullYear() === now.getFullYear();
      }).reduce((sum, q) => sum + parseFloat(q.price || 0), 0);
      
      const lastMonth = quotes.filter(q => {
        const quoteDate = new Date(q.approved_date || q.date);
        const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1);
        return quoteDate.getMonth() === lastMonthDate.getMonth() && quoteDate.getFullYear() === lastMonthDate.getFullYear();
      }).reduce((sum, q) => sum + parseFloat(q.price || 0), 0);
      
      setStats({
        totalApproved: quotes.length,
        totalRevenue,
        thisMonth,
        lastMonth
      });
      
      // Prepare chart data - group by date and cumulative sum
      const dateMap = {};
      quotes.forEach(quote => {
        const date = quote.approved_date || quote.date;
        if (!dateMap[date]) {
          dateMap[date] = 0;
        }
        dateMap[date] += parseFloat(quote.price || 0);
      });
      
      // Sort dates and create cumulative data
      const sortedDates = Object.keys(dateMap).sort();
      let cumulative = 0;
      const data = sortedDates.map(date => {
        cumulative += dateMap[date];
        return {
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          revenue: parseFloat(cumulative.toFixed(2)),
          daily: parseFloat(dateMap[date].toFixed(2))
        };
      });
      
      setChartData(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem' }}>Loading analytics...</div>;
  }

  return (
    <div style={{ padding: 'clamp(1rem, 4vw, 2rem)', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 'clamp(1rem, 4vw, 2rem)' }}>
          <h1 style={{ fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', color: '#333', marginBottom: '0.5rem' }}>
            Performance Analytics
          </h1>
          <p style={{ fontSize: 'clamp(0.9rem, 3vw, 1.1rem)', color: '#666' }}>
            Track your approved quotes and revenue over time
          </p>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
          gap: 'clamp(0.75rem, 3vw, 1.5rem)',
          marginBottom: 'clamp(1rem, 4vw, 2rem)'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: 'clamp(1rem, 3vw, 1.5rem)',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '2px solid #e9ecef'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <CheckCircle size={24} style={{ color: '#28a745', flexShrink: 0 }} />
              <span style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)', color: '#666' }}>Total Approved</span>
            </div>
            <p style={{ fontSize: 'clamp(1.75rem, 6vw, 2.5rem)', fontWeight: 'bold', color: '#333', margin: 0 }}>
              {stats.totalApproved}
            </p>
            <p style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)', color: '#999', marginTop: '0.5rem' }}>Quotes</p>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: 'clamp(1rem, 3vw, 1.5rem)',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '2px solid #e9ecef'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <DollarSign size={24} style={{ color: '#28a745', flexShrink: 0 }} />
              <span style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)', color: '#666' }}>Total Revenue</span>
            </div>
            <p style={{ fontSize: 'clamp(1.75rem, 6vw, 2.5rem)', fontWeight: 'bold', color: '#28a745', margin: 0 }}>
              ${stats.totalRevenue.toFixed(2)}
            </p>
            <p style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)', color: '#999', marginTop: '0.5rem' }}>All time</p>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: 'clamp(1rem, 3vw, 1.5rem)',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '2px solid #e9ecef'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <Calendar size={24} style={{ color: '#4A90E2', flexShrink: 0 }} />
              <span style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)', color: '#666' }}>This Month</span>
            </div>
            <p style={{ fontSize: 'clamp(1.75rem, 6vw, 2.5rem)', fontWeight: 'bold', color: '#4A90E2', margin: 0 }}>
              ${stats.thisMonth.toFixed(2)}
            </p>
            <p style={{ fontSize: 'clamp(0.75rem, 2vw, 0.9rem)', color: stats.thisMonth >= stats.lastMonth ? '#28a745' : '#dc3545', marginTop: '0.5rem' }}>
              {stats.thisMonth >= stats.lastMonth ? '↑' : '↓'} vs last month (${stats.lastMonth.toFixed(2)})
            </p>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: 'clamp(1rem, 3vw, 1.5rem)',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '2px solid #e9ecef'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <TrendingUp size={24} style={{ color: '#28a745', flexShrink: 0 }} />
              <span style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)', color: '#666' }}>Average Quote</span>
            </div>
            <p style={{ fontSize: 'clamp(1.75rem, 6vw, 2.5rem)', fontWeight: 'bold', color: '#333', margin: 0 }}>
              ${stats.totalApproved > 0 ? (stats.totalRevenue / stats.totalApproved).toFixed(2) : '0.00'}
            </p>
            <p style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)', color: '#999', marginTop: '0.5rem' }}>Per approved quote</p>
          </div>
        </div>

        {/* Revenue Chart */}
        {chartData.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            marginBottom: '2rem'
          }}>
            <h2 style={{ fontSize: '1.8rem', color: '#333', marginBottom: '1.5rem' }}>
              Cumulative Revenue Growth
            </h2>
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#28a745" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#28a745" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                <XAxis 
                  dataKey="date" 
                  stroke="#666"
                  style={{ fontSize: '0.9rem' }}
                />
                <YAxis 
                  stroke="#666"
                  style={{ fontSize: '0.9rem' }}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '2px solid #28a745',
                    borderRadius: '8px',
                    padding: '10px'
                  }}
                  formatter={(value) => [`$${value}`, 'Total Revenue']}
                />
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#28a745" 
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorRevenue)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Approved Quotes List */}
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ fontSize: '1.8rem', color: '#333', marginBottom: '1.5rem' }}>
            All Approved Quotes
          </h2>
          
          {approvedQuotes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
              <CheckCircle size={64} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
              <p style={{ fontSize: '1.2rem' }}>No approved quotes yet</p>
              <p>Start approving quotes from your history to see them here!</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.9rem', fontWeight: '600', color: '#666' }}>Quote #</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.9rem', fontWeight: '600', color: '#666' }}>Customer</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.9rem', fontWeight: '600', color: '#666' }}>Service</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.9rem', fontWeight: '600', color: '#666' }}>Location</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.9rem', fontWeight: '600', color: '#666' }}>Date</th>
                    <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.9rem', fontWeight: '600', color: '#666' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {approvedQuotes.map((quote, index) => (
                    <tr key={quote.id} style={{ 
                      borderBottom: '1px solid #e9ecef',
                      backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                    }}>
                      <td style={{ padding: '1rem', fontWeight: '600', color: '#4A90E2' }}>{quote.quote_number}</td>
                      <td style={{ padding: '1rem', color: '#333' }}>{quote.customer_name}</td>
                      <td style={{ padding: '1rem', color: '#666' }}>{quote.service_type}</td>
                      <td style={{ padding: '1rem', color: '#666' }}>{quote.location || '-'}</td>
                      <td style={{ padding: '1rem', color: '#666' }}>{quote.approved_date || quote.date}</td>
                      <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', color: '#28a745' }}>
                        ${parseFloat(quote.price).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;